import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { IAccountPermission, IApplicationPermission, IModulePermission, IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { EXCHANGE_RATES, PERMISSION_MAPPER, CurrencyCode, CURRENCY_SYMBOLS, CURRENCY_FORMATTERS } from "../shared/constants";
import {
  IPlatformAccount,
  IAccountsEntity,
} from "../features/Configurations/Integration/interface";
import dayjs from "dayjs";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function hasPermission(
  permission: string,
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  if (!permissions) return false;
  if (permissions === "*") return true;
  if (permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS) return true;
  if (typeof permissions === "string") {
    return permissions.includes(permission);
  }
  return (permissions[category as keyof IPermissionCategories] as IModulePermission[] | IApplicationPermission[] | IAccountPermission[])?.some((p) => p.codename === permission);
}

export function checkAllPermissions(
  permissionsToCheck: string[],
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  return permissionsToCheck.every((perm) =>
    hasPermission(perm, category, permissions)
  );
}

export function checkAnyPermissions(
  permissionsToCheck: string[],
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  return permissionsToCheck.some((perm) =>
    hasPermission(perm, category, permissions)
  );
}

export function checkPermission(
  permission: string,
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  return hasPermission(permission, category, permissions);
}

export const formatNumber = (value: number) => {
  // Round to 2 decimal places to handle floating-point precision issues
  const rounded = Math.round(value * 100) / 100;

  // Check if the rounded value is effectively an integer
  if (rounded % 1 === 0) {
    return rounded.toString();
  } else {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  }
};

// Utility function to convert IPlatformAccount from camelCase to snake_case
export const convertIPlatformAccountToEntity = (
  account: IAccountsEntity | IPlatformAccount
): IAccountsEntity => {
  return {
    id:
      (account.id ||
        (account as IAccountsEntity).publisher_id ||
        (account as IPlatformAccount).publisherId ||
        "") as string,
    name: account.name || "",
    currency_code:
      ((account as IAccountsEntity).currency_code ||
        (account as IPlatformAccount).currencyCode ||
        (account as IPlatformAccount).currency ||
        "") as string,
    publisher_id:
      (account as IAccountsEntity).publisher_id ||
      (account as IPlatformAccount).publisherId,
    reporting_time_zone:
      (account as IAccountsEntity).reporting_time_zone ||
      (account as IPlatformAccount).reportingTimeZone ||
      (account as IPlatformAccount).timeZone ||
      (account as IPlatformAccount).time_zone,
    status: ((account as IAccountsEntity).status ||
      (account as IPlatformAccount).status) as string || "",
  };
};

// Utility function to convert accounts array from IPlatformAccount to IAccountsEntity
export const convertAccountsToEntities = (
  accounts: IPlatformAccount[]
): IAccountsEntity[] => {
  return accounts.map((account) => convertIPlatformAccountToEntity(account));
};

export function getTimezoneOffsetString(timezoneStr: string): string {
  if (!timezoneStr) return "";
  const offsetMinutes = dayjs().tz(timezoneStr).utcOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
}


export const getNetworkWiseRevenue = (networkWiseReveue: { [key: string]: number }) => Object.entries(networkWiseReveue || {})
  .map(([network, amount]) => {
    const numericAmount = Number(amount);
    return {
      network,
      amount,
      numericAmount,
      isNumeric: Number.isFinite(numericAmount),
    };
  })
  .filter((e) => {
    if (!e.isNumeric) return true;
    return e.numericAmount !== 0;
  });

export const formatCurrency = (
  amount: number,
  currencyCode: CurrencyCode = "USD"
): string => {
  return CURRENCY_FORMATTERS[currencyCode]?.format(amount) || amount.toString();
};

export const convertCurrency = (
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): number => {
  // Convert from source currency to USD, then to target currency
  const amountInUSD = amount / EXCHANGE_RATES[fromCurrency];
  return amountInUSD * EXCHANGE_RATES[toCurrency];
};

export const getCurrencySymbol = (currencyCode: CurrencyCode = "USD"): string => {
  return CURRENCY_SYMBOLS[currencyCode] || "$";
};

// Reads a currency-specific numeric field from an object using the pattern `${prefix}_${currency}`.
// e.g. getAmountByCurrency(row, "spend", "AUD") → row.spend_aud ?? 0
export const getAmountByCurrency = (obj: Record<string, unknown>, prefix: string, currency: string): number => {
  const val = obj?.[`${prefix}_${currency.toLowerCase()}`];
  return typeof val === "number" ? val : Number(val) || 0;
};